import moment from "moment";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";

const DashBoard = process.env.REACT_APP_DASHBOARD_URL || "";
const Dash_Board = api(DashBoard);

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

// Define the response type
interface LatestCase {
  caseInfoId: number;
  patientName: string;
  caseInfoText: string | null;
  lastModifiedDate: string;
  caseinfo: any;
  customerAccountNo?: string;
}

// Correct function to call GET API
export const getLatestCases = async (limit: number): Promise<LatestCase[]> => {
  try {
    const response = await Dash_Board.get(
      `/api/web/dashboard/latest-cases?limit=${limit}`,
    );
    return response.data.result; // Assuming response.data contains result
  } catch (error) {
    console.error("Error fetching latest cases:", error);
    handleApiError(error); // Optional: if you have this utility
    throw new Error("Unable to fetch latest cases. Please try again later.");
  }
};

export const getCaseActionsSummary = async (interval: any): Promise<any[]> => {
  try {
    const response = await Dash_Board.get(
      `/api/web/dashboard/case-actions?interval=${interval}`,
    );
    return response.data?.result || [];
  } catch (error) {
    console.error("Error fetching case actions:", error);
    handleApiError(error);
    throw new Error("Unable to fetch case actions. Please try again later.");
  }
};

// Add this to ActionStatusApi.ts

export interface AppointmentSummary {
  caseAppointmentId: number;
  appointmentType: string;
  appointmentValues_Scheduled: number;
  appointmentValues_Completed: number;
  appointmentValues_Cancelled: number;
  appointmentValues_OnTime?: number;
}

export const getAppointmentSummary = async (): Promise<
  AppointmentSummary[]
> => {
  try {
    const response = await Dash_Board.get("/api/web/dashboard/appointments");
    return response.data.result;
  } catch (error) {
    console.error("Error fetching appointment summary:", error);
    handleApiError(error);
    throw new Error(
      "Unable to fetch appointment summary. Please try again later.",
    );
  }
};
// TypeScript interface based on your Swagger response
export interface CaseSummaryItem {
  tenantId: number;
  pickListItemId: number;
  stageName: string;
  countOfCases: number;
}

// New API function to fetch case summary
export const getCaseSummary = async (
  interval: string,
): Promise<CaseSummaryItem[]> => {
  try {
    const response = await Dash_Board.get(
      `/api/web/dashboard/case-summary?interval=${interval}`,
    );
    return response.data.result; // assuming the API responds with { result: [...] }
  } catch (error) {
    console.error("Error fetching case summary:", error);
    handleApiError(error);
    throw new Error("Unable to fetch case summary. Please try again later.");
  }
};
// TypeScript interface based on your response
export interface QmeDoctorCaseVolume {
  qmeDoctorContactId: number;
  qmeDoctorName: string;
  countOfAssignedCases: number;
}

// API call function
export const getQmeDoctorCaseVolume = async (
  limit: number,
  interval: string,
): Promise<any> => {
  try {
    const response = await Dash_Board.get(
      `/api/web/dashboard/case-volume/qme-doctor?limit=${limit}&interval=${interval}`,
    );
    return response.data.result;
  } catch (error) {
    console.error("Error fetching QME doctor case volume:", error);
    handleApiError(error);
    throw new Error(
      "Unable to fetch QME doctor case volume. Please try again later.",
    );
  }
};

export interface QmeDoctorCaseVolumeByID {
  caseStageName: string;
  countOfAssignedCasesByStage: number;
}

export const getQmeDoctorCaseVolumeByID = async (
  qmeDoctorContactId: number,
  interval: string,
): Promise<QmeDoctorCaseVolumeByID[]> => {
  try {
    const response = await Dash_Board.get(
      `/api/web/dashboard/case-volume/qme-doctor/drilldown?qmeDoctorContactId=${qmeDoctorContactId}&interval=${interval}`,
    );
    return response.data.result;
  } catch (error) {
    console.error("Error fetching QME doctor case volume:", error);
    handleApiError(error);
    throw new Error(
      "Unable to fetch QME doctor case volume. Please try again later.",
    );
  }
};

export const getRushNotesSummary = async (limit: any): Promise<any> => {
  try {
    const response = await Dash_Board.get<ApiResponse<any>>(
      `/api/web/dashboard/latest-rush-notes?limit=${limit}`,
    );

    return response.data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getHRESummaryByQMEDoctor = async (
  limit: any,
  interval: any,
): Promise<any> => {
  try {
    const response = await Dash_Board.get<ApiResponse<any>>(
      `/api/web/dashboard/hre-summary/by-qme-doctor?limit=${limit}&interval=${interval}`,
    );

    return response.data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getHREOverviewSummary = async (interval: any): Promise<any> => {
  try {
    const response = await Dash_Board.get<ApiResponse<any>>(
      `/api/web/dashboard/hre-status-summary?interval=${interval}`,
    );

    return response.data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getAppointmentAnalyticsView = async (): Promise<any> => {
  try {
    const response = await Dash_Board.get<ApiResponse<any>>(
      `/api/web/dashboard/appointments/analytics`,
    );

    return response.data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getAllRushNotes = async (data: any): Promise<any> => {
  try {
    const response = await Dash_Board.post(
      '/api/web/dashboard/rush-notes', data
    );

    return response.data?.result;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};